import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  createMeetingMinutesMeeting,
  fetchMeetingMinutesById,
  fetchMeetingMinutesTemplates,
} from "../../../api/meetingMinutes";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../components/utils/ResourceError";
import {
  MeetingMinutesShell,
  mmCardClass,
  mmPrimaryButtonClass,
  mmSecondaryButtonClass,
} from "./MeetingMinutesShell";
import { MeetingMinutesTemplatePicker } from "./MeetingMinutesTemplatePicker";

export const MeetingMinutesNewMeeting = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [meetingMinutes, setMeetingMinutes] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    meeting_date: new Date().toISOString().split("T")[0],
    copy_from_meeting: null,
    template_id: null,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [data, templatesResponse] = await Promise.all([
          fetchMeetingMinutesById(id),
          fetchMeetingMinutesTemplates({ per_page: 100 }),
        ]);
        setMeetingMinutes(data);
        setTemplates(templatesResponse.data || []);
      } catch (err) {
        setError(true);
        toast.error(t("meeting_minutes.load_error"));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const latestMeetingNumber =
        meetingMinutes.meetings?.length > 0
          ? Math.max(...meetingMinutes.meetings.map((m) => m.meeting_number || 1))
          : 0;
      const queryParams = {};
      if (formData.copy_from_meeting) {
        queryParams.copy_from_meeting_id = formData.copy_from_meeting;
      } else if (formData.template_id) {
        queryParams.template_id = formData.template_id;
      }
      const newMeeting = await createMeetingMinutesMeeting(
        id,
        {
          meeting_number: latestMeetingNumber + 1,
          meeting_date: formData.meeting_date,
        },
        queryParams
      );
      if (newMeeting?.errors) throw new Error(newMeeting.errors.join?.(", "));
      toast.success(t("meeting_minutes.meeting.created"));
      navigate(`/dashboard/meeting-minutes/${id}/meetings/${newMeeting.id}`);
    } catch (err) {
      toast.error(t("meeting_minutes.meeting.create_error"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <ResourceLoading />;
  if (error || !meetingMinutes) return <ResourceError />;

  const selectedTemplate = templates.find((row) => row.id === formData.template_id);

  return (
    <MeetingMinutesShell
      title={t("meeting_minutes.add_meeting")}
      subtitle={meetingMinutes.title}
      backTo={`/dashboard/meeting-minutes/${id}`}
      backLabel={t("Back")}
    >
      <form onSubmit={handleSubmit} className={`${mmCardClass()} space-y-8 px-6 py-6`}>
        <div>
          <label className="mb-2 block text-sm font-semibold text-slate-700">
            {t("meeting_minutes.meeting_date")} *
          </label>
          <div className="mb-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() =>
                setFormData({
                  ...formData,
                  meeting_date: new Date().toISOString().split("T")[0],
                })
              }
              className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              {t("meeting_minutes.date.today")}
            </button>
          </div>
          <input
            type="date"
            required
            value={formData.meeting_date}
            onChange={(e) => setFormData({ ...formData, meeting_date: e.target.value })}
            className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm focus:border-[#1463C7] focus:outline-none focus:ring-2 focus:ring-[#1463C7]/20"
          />
        </div>

        <div>
          <h3 className="mb-3 text-sm font-semibold text-slate-900">
            {t("meeting_minutes.templates.use_template")}
          </h3>
          <MeetingMinutesTemplatePicker
            templates={templates}
            selectedId={formData.copy_from_meeting ? null : formData.template_id}
            onSelect={(template) =>
              setFormData({
                ...formData,
                template_id: template?.id || null,
                copy_from_meeting: null,
              })
            }
          />
          {selectedTemplate?.groups?.length ? (
            <div className="mt-4 rounded-2xl bg-slate-50 px-4 py-3 text-sm text-slate-600">
              {t("meeting_minutes.templates.will_load", {
                count: selectedTemplate.groups.length,
              })}
            </div>
          ) : null}
        </div>

        <div>
          <h3 className="mb-3 text-sm font-semibold text-slate-900">
            {t("meeting_minutes.meeting.copy_from_previous")}
          </h3>
          <select
            value={formData.copy_from_meeting || ""}
            onChange={(e) =>
              setFormData({
                ...formData,
                copy_from_meeting: e.target.value ? parseInt(e.target.value, 10) : null,
                template_id: null,
              })
            }
            className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm focus:border-[#1463C7] focus:outline-none focus:ring-2 focus:ring-[#1463C7]/20"
          >
            <option value="">{t("meeting_minutes.meeting.no_copy")}</option>
            {meetingMinutes.meetings?.map((meeting) => (
              <option key={meeting.id} value={meeting.id}>
                {t("meeting_minutes.meeting_number")} {meeting.meeting_number} – {meeting.meeting_date}
              </option>
            ))}
          </select>
        </div>

        <div className="flex justify-end gap-3 border-t border-slate-100 pt-6">
          <button
            type="button"
            onClick={() => navigate(`/dashboard/meeting-minutes/${id}`)}
            className={mmSecondaryButtonClass()}
          >
            {t("Cancel")}
          </button>
          <button type="submit" disabled={saving} className={mmPrimaryButtonClass()}>
            {saving ? t("Saving...") : t("Create")}
          </button>
        </div>
      </form>
    </MeetingMinutesShell>
  );
};
